import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';

const formatCurrency = value =>
  `${Number(value || 0).toLocaleString('nb-NO')} kr`;

const BackHeader = ({ title, description, onBack }) => (
  <div className="bg-white px-8 py-6 border-b border-slate-200 flex items-center gap-4">
    <button onClick={onBack} className="text-slate-400 hover:text-slate-900">
      <SafeIcon icon="⬅️" />
    </button>
    <div>
      <h1 className="text-xl font-bold">{title}</h1>
      <p className="text-xs text-slate-500">{description}</p>
    </div>
  </div>
);

const EmptyState = ({ title, description }) => (
  <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
    <p className="font-bold text-slate-700">{title}</p>
    <p className="text-sm text-slate-500 mt-2">{description}</p>
  </div>
);

export const CountryDepartmentsView = ({ onBack }) => {
  const [countries, setCountries] = useState([
    { id: 'no', name: 'Norge', code: 'NO', departments: ['Salg', 'Support', 'Finance'] },
    { id: 'se', name: 'Sverige', code: 'SE', departments: [] },
    { id: 'dk', name: 'Danmark', code: 'DK', departments: [] }
  ]);
  const [countryName, setCountryName] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [department, setDepartment] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('no');

  const addCountry = event => {
    event.preventDefault();
    const name = countryName.trim();
    const code = countryCode.trim().toUpperCase();

    if (!name || !code) return;

    setCountries(current => [
      ...current,
      { id: code.toLowerCase(), name, code, departments: [] }
    ]);
    setCountryName('');
    setCountryCode('');
  };

  const addDepartment = event => {
    event.preventDefault();
    const value = department.trim();

    if (!value) return;

    setCountries(current =>
      current.map(country =>
        country.id === selectedCountry && !country.departments.includes(value)
          ? { ...country, departments: [...country.departments, value] }
          : country
      )
    );
    setDepartment('');
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <BackHeader
        title="Land & avdelinger"
        description="Administrer tilgjengelige land og interne avdelinger"
        onBack={onBack}
      />

      <div className="max-w-6xl mx-auto mt-8 px-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={addCountry} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h2 className="font-bold">Legg til land</h2>
          <p className="text-xs text-slate-500 mt-1">Brukes når plattformen utvides til nye markeder.</p>

          <label className="block text-xs font-bold text-slate-500 mt-5 mb-1">Navn</label>
          <input
            value={countryName}
            onChange={event => setCountryName(event.target.value)}
            placeholder="F.eks. Finland"
            className="w-full p-3 border border-slate-200 rounded-xl text-sm"
          />

          <label className="block text-xs font-bold text-slate-500 mt-4 mb-1">Landskode</label>
          <input
            value={countryCode}
            onChange={event => setCountryCode(event.target.value)}
            maxLength={2}
            placeholder="FI"
            className="w-full p-3 border border-slate-200 rounded-xl text-sm uppercase"
          />

          <button className="w-full mt-5 py-3 rounded-xl bg-slate-900 text-white text-sm font-bold">
            Legg til land
          </button>
        </form>

        <form onSubmit={addDepartment} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h2 className="font-bold">Legg til avdeling</h2>
          <p className="text-xs text-slate-500 mt-1">Opprett en avdeling under valgt land.</p>

          <label className="block text-xs font-bold text-slate-500 mt-5 mb-1">Land</label>
          <select
            value={selectedCountry}
            onChange={event => setSelectedCountry(event.target.value)}
            className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-white"
          >
            {countries.map(country => (
              <option key={country.id} value={country.id}>
                {country.name} ({country.code})
              </option>
            ))}
          </select>

          <label className="block text-xs font-bold text-slate-500 mt-4 mb-1">Avdelingsnavn</label>
          <input
            value={department}
            onChange={event => setDepartment(event.target.value)}
            placeholder="F.eks. Customer Success"
            className="w-full p-3 border border-slate-200 rounded-xl text-sm"
          />

          <button className="w-full mt-5 py-3 rounded-xl bg-blue-600 text-white text-sm font-bold">
            Legg til avdeling
          </button>
        </form>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h2 className="font-bold mb-4">Registrerte land</h2>
          <div className="space-y-3">
            {countries.map(country => (
              <button
                key={country.id}
                onClick={() => setSelectedCountry(country.id)}
                className={`w-full text-left p-4 rounded-xl border ${
                  selectedCountry === country.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 bg-white'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span className="font-bold">{country.name}</span>
                  <span className="text-xs font-black text-slate-400">{country.code}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {country.departments.length === 0 ? (
                    <span className="text-xs text-slate-400">Ingen avdelinger</span>
                  ) : (
                    country.departments.map(item => (
                      <span key={item} className="px-2 py-1 rounded bg-slate-100 text-xs text-slate-600">
                        {item}
                      </span>
                    ))
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const PricingDiscountsView = ({
  onBack,
  plans,
  discountCodes,
  discountCodesLoading,
  loadDiscountCodes,
  createDiscountCode,
  updateDiscountCode
}) => {
  const emptyDiscount = {
    name: '',
    code: '',
    plan_id: '',
    percent_off: '',
    amount_off: '',
    starts_at: '',
    ends_at: '',
    duration_days: '',
    is_active: true
  };
  const [form, setForm] = useState(emptyDiscount);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadDiscountCodes();
  }, [loadDiscountCodes]);

  const resetForm = () => {
    setForm(emptyDiscount);
    setMessage('');
  };

  const submitDiscount = async event => {
    event.preventDefault();
    const percent = Number(form.percent_off || 0);
    const amount = Number(form.amount_off || 0);

    if (!form.name.trim() || !form.code.trim()) {
      setMessage('Navn og rabattkode må fylles ut.');
      return;
    }

    if (percent <= 0 && amount <= 0) {
      setMessage('Angi minst én rabatt: prosent eller beløp i kroner.');
      return;
    }

    if (form.ends_at && form.starts_at && form.ends_at < form.starts_at) {
      setMessage('Sluttdato kan ikke være før startdato.');
      return;
    }

    setSaving(true);
    setMessage('');
    const result = await createDiscountCode(form);
    setSaving(false);

    if (!result.success) {
      setMessage(result.error?.message || 'Kunne ikke lagre rabattkoden.');
      return;
    }

    resetForm();
    setMessage('Rabattkoden er opprettet og klar til bruk.');
  };

  const toggleDiscount = async discount => {
    const result = await updateDiscountCode(discount.id, {
      is_active: !discount.is_active
    });

    if (!result.success) {
      setMessage(result.error?.message || 'Kunne ikke oppdatere rabattkoden.');
    }
  };

  const describeDiscount = discount => {
    const parts = [];

    if (Number(discount.percent_off) > 0) {
      parts.push(`${Number(discount.percent_off)} %`);
    }

    if (Number(discount.amount_off) > 0) {
      parts.push(`${formatCurrency(discount.amount_off)}`);
    }

    return parts.join(' + ');
  };

  const describeValidity = discount => {
    if (discount.duration_days) {
      return `${discount.duration_days} dager fra første bruk`;
    }

    if (discount.starts_at && discount.ends_at) {
      return `${discount.starts_at} – ${discount.ends_at}`;
    }

    if (discount.starts_at) {
      return `Fra ${discount.starts_at}`;
    }

    if (discount.ends_at) {
      return `Til ${discount.ends_at}`;
    }

    return 'Ingen tidsbegrensning';
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <BackHeader
        title="Priser og rabatter"
        description="Opprett egne rabattkoder med prosent, fast beløp og gyldighet"
        onBack={onBack}
      />

      <div className="max-w-7xl mx-auto mt-8 px-6 pb-10 grid grid-cols-1 xl:grid-cols-3 gap-6">
        <form onSubmit={submitDiscount} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm h-fit">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-bold text-lg">Opprett rabattkode</h2>
              <p className="text-xs text-slate-500 mt-1">Koden kan brukes på alle planer eller en bestemt plan.</p>
            </div>
            <button
              type="button"
              onClick={resetForm}
              className="text-xs font-bold text-slate-400 hover:text-slate-700"
            >
              Nullstill
            </button>
          </div>

          <label className="block text-xs font-bold text-slate-500 mt-5 mb-1">Rabattnavn</label>
          <input
            required
            value={form.name}
            onChange={event => setForm({ ...form, name: event.target.value })}
            placeholder="F.eks. Sommeravtale 2026"
            className="w-full p-3 border border-slate-200 rounded-xl text-sm"
          />

          <label className="block text-xs font-bold text-slate-500 mt-4 mb-1">Rabattkode</label>
          <input
            required
            value={form.code}
            onChange={event => setForm({ ...form, code: event.target.value.toUpperCase() })}
            placeholder="F.eks. SOMMER26"
            className="w-full p-3 border border-slate-200 rounded-xl text-sm uppercase"
          />

          <label className="block text-xs font-bold text-slate-500 mt-4 mb-1">Gjelder abonnement</label>
          <select
            value={form.plan_id}
            onChange={event => setForm({ ...form, plan_id: event.target.value })}
            className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-white"
          >
            <option value="">Alle abonnementer</option>
            {(plans || []).map(plan => (
              <option key={plan.id} value={plan.id}>{plan.name}</option>
            ))}
          </select>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Rabatt i %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={form.percent_off}
                onChange={event => setForm({ ...form, percent_off: event.target.value })}
                placeholder="20"
                className="w-full p-3 border border-slate-200 rounded-xl text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Fast beløp (kr)</label>
              <input
                type="number"
                min="0"
                value={form.amount_off}
                onChange={event => setForm({ ...form, amount_off: event.target.value })}
                placeholder="250"
                className="w-full p-3 border border-slate-200 rounded-xl text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Gyldig fra</label>
              <input
                type="date"
                value={form.starts_at}
                onChange={event => setForm({ ...form, starts_at: event.target.value })}
                className="w-full p-3 border border-slate-200 rounded-xl text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Gyldig til</label>
              <input
                type="date"
                value={form.ends_at}
                onChange={event => setForm({ ...form, ends_at: event.target.value })}
                className="w-full p-3 border border-slate-200 rounded-xl text-sm"
              />
            </div>
          </div>

          <label className="block text-xs font-bold text-slate-500 mt-4 mb-1">
            Varighet fra første bruk (dager)
          </label>
          <input
            type="number"
            min="1"
            value={form.duration_days}
            onChange={event => setForm({ ...form, duration_days: event.target.value })}
            placeholder="F.eks. 90"
            className="w-full p-3 border border-slate-200 rounded-xl text-sm"
          />
          <p className="text-[11px] text-slate-400 mt-1">
            Valgfritt. Brukes når rabatten skal løpe en begrenset periode fra kunden tar den i bruk.
          </p>

          <label className="flex items-center gap-2 mt-4 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={event => setForm({ ...form, is_active: event.target.checked })}
            />
            Aktiver rabattkoden nå
          </label>

          {message && (
            <p className={`mt-4 text-xs font-medium ${
              message.includes('opprettet') ? 'text-emerald-700' : 'text-red-600'
            }`}>
              {message}
            </p>
          )}

          <button
            disabled={saving}
            className="w-full mt-5 py-3 rounded-xl bg-slate-900 text-white text-sm font-bold disabled:opacity-60"
          >
            {saving ? 'Lagrer...' : 'Opprett rabattkode'}
          </button>
        </form>

        <section className="xl:col-span-2 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(plans || []).map(plan => (
              <div key={plan.id} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <p className="text-xs uppercase tracking-widest text-slate-400 font-black">{plan.name}</p>
                <p className="text-2xl font-black mt-2">{formatCurrency(plan.price)}</p>
                <p className="text-xs text-slate-500 mt-1">Ordinær månedspris</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="font-bold">Opprettede rabattkoder</h2>
                <p className="text-xs text-slate-500 mt-1">Slå av en kode uten å miste historikken.</p>
              </div>
              <button
                onClick={loadDiscountCodes}
                className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200"
              >
                Oppdater
              </button>
            </div>

            {discountCodesLoading ? (
              <div className="p-10 text-center text-sm text-slate-500">Laster rabattkoder...</div>
            ) : (discountCodes || []).length === 0 ? (
              <EmptyState
                title="Ingen rabattkoder er opprettet"
                description="Opprett den første rabattkoden i skjemaet til venstre."
              />
            ) : (
              <div className="divide-y divide-slate-100">
                {discountCodes.map(discount => (
                  <div key={discount.id} className="p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center flex-wrap gap-2">
                        <p className="font-bold">{discount.name}</p>
                        <span className="px-2 py-1 rounded bg-slate-100 text-xs font-black tracking-wider">
                          {discount.code}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          discount.is_active
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          {discount.is_active ? 'Aktiv' : 'Deaktivert'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-slate-500">
                        <span>Rabatt: <b className="text-slate-700">{describeDiscount(discount)}</b></span>
                        <span>
                          Plan: <b className="text-slate-700">
                            {(plans || []).find(plan => plan.id === discount.plan_id)?.name || 'Alle abonnementer'}
                          </b>
                        </span>
                        <span>Gyldighet: <b className="text-slate-700">{describeValidity(discount)}</b></span>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleDiscount(discount)}
                      className={`px-4 py-2 rounded-lg text-xs font-bold ${
                        discount.is_active
                          ? 'bg-red-50 text-red-700 hover:bg-red-100'
                          : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                      }`}
                    >
                      {discount.is_active ? 'Deaktiver' : 'Aktiver'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export const PartnersView = ({ onBack, partners, pendingPartners }) => {
  const [pending, setPending] = useState(pendingPartners || []);
  const [approved, setApproved] = useState(partners || []);

  const approve = partner => {
    setPending(current => current.filter(item => item.id !== partner.id));
    setApproved(current => [...current, { ...partner, status: 'Active' }]);
  };

  const reject = partner => {
    setPending(current => current.filter(item => item.id !== partner.id));
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <BackHeader
        title="Partnere"
        description="Administrer partnerstatus, provisjon og partnerkunder"
        onBack={onBack}
      />

      <div className="max-w-6xl mx-auto mt-8 px-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-black">Totalt</p>
            <p className="text-3xl font-black mt-2">{approved.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-black">Ventende</p>
            <p className="text-3xl font-black mt-2">{pending.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-black">Aktive kunder</p>
            <p className="text-3xl font-black mt-2">
              {approved.reduce((sum, partner) => sum + (partner.customers || []).length, 0)}
            </p>
          </div>
        </div>

        <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-bold">Ventende partnerforespørsler</h2>
          </div>
          {pending.length === 0 ? (
            <div className="p-8 text-sm text-slate-500">Ingen ventende partnerforespørsler.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {pending.map(partner => (
                <div key={partner.id} className="p-5 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-bold">{partner.name || 'Ukjent partner'}</p>
                    <p className="text-xs text-slate-500">{partner.email || 'Ingen e-post oppgitt'}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => approve(partner)} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold">
                      Godkjenn
                    </button>
                    <button onClick={() => reject(partner)} className="px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs font-bold">
                      Avvis
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-bold">Aktive partnere</h2>
          </div>
          {approved.length === 0 ? (
            <div className="p-8 text-sm text-slate-500">Ingen aktive partnere.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 font-bold text-slate-600">Partner</th>
                    <th className="px-6 py-4 font-bold text-slate-600">Provisjon</th>
                    <th className="px-6 py-4 font-bold text-slate-600">Kunder</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {approved.map(partner => (
                    <tr key={partner.id}>
                      <td className="px-6 py-4 font-bold">{partner.name}</td>
                      <td className="px-6 py-4">{Number(partner.commissionRate || 0) * 100} %</td>
                      <td className="px-6 py-4">{(partner.customers || []).length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export const SystemReportsView = ({ onBack, customers, profiles, auditLog }) => {
  const metrics = useMemo(() => ({
    customers: customers.length,
    activeCustomers: customers.filter(customer => customer.status === 'Active').length,
    users: profiles.length,
    auditEvents: auditLog.length,
    mrr: customers.reduce((sum, customer) => sum + Number(customer.monthly_value || 0), 0)
  }), [customers, profiles, auditLog]);

  const exportReport = () => {
    const report = {
      generated_at: new Date().toISOString(),
      metrics,
      customers,
      auditLog
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `saleswork-systemrapport-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <BackHeader
        title="Systemrapporter"
        description="Oversikt over plattformbruk, kunder og administrative hendelser"
        onBack={onBack}
      />

      <div className="max-w-7xl mx-auto mt-8 px-6 space-y-6">
        <div className="flex justify-end">
          <button onClick={exportReport} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold">
            Eksporter rapport
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[
            ['Kunder', metrics.customers],
            ['Aktive kunder', metrics.activeCustomers],
            ['Plattformbrukere', metrics.users],
            ['Audit-hendelser', metrics.auditEvents],
            ['Månedlig verdi', formatCurrency(metrics.mrr)]
          ].map(([label, value]) => (
            <div key={label} className="bg-white rounded-2xl border border-slate-200 p-5">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-black">{label}</p>
              <p className="text-2xl font-black mt-2">{value}</p>
            </div>
          ))}
        </div>

        <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-bold">Siste administrative hendelser</h2>
          </div>
          {auditLog.length === 0 ? (
            <div className="p-8 text-sm text-slate-500">Ingen audit-hendelser registrert.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {auditLog.slice(0, 25).map(event => (
                <div key={event.id} className="px-6 py-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-bold text-sm">{event.action || 'Ukjent handling'}</p>
                    <p className="text-xs text-slate-500">
                      {event.entity_type || 'System'} · {event.entity_id || '—'}
                    </p>
                  </div>
                  <time className="text-xs text-slate-500">
                    {event.created_at ? new Date(event.created_at).toLocaleString('nb-NO') : '—'}
                  </time>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};